import { Card } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GripVertical, Trash2, Check, Hash } from '@/components/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export type ChecklistItemType = 'YesNo' | 'Text' | 'TextArea' | 'Number'

export type FieldItem = {
  id: string
  type: ChecklistItemType
  label: string
  isRequired: boolean
  sortOrder: number
}

interface ChecklistTemplateItemCardProps {
  field: FieldItem
  isArranging: boolean
  onUpdate: (id: string, updates: Partial<FieldItem>) => void
  onRemove: (id: string) => void
}

export function ChecklistTemplateItemCard({
  field,
  isArranging,
  onUpdate,
  onRemove,
}: ChecklistTemplateItemCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="flex gap-4 items-start px-4 py-4"
    >
      {/* Drag Handle */}
      {isArranging && (
        <button
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing shrink-0 touch-none mt-0.5"
          aria-label="Drag to reorder"
        >
          <GripVertical className="size-6 text-content-primary-subtle" />
        </button>
      )}

      {/* Field Content */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {/* Type Selector */}
        <Select
          value={field.type}
          onValueChange={(value: string) =>
            onUpdate(field.id, { type: value as ChecklistItemType })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="YesNo">
              <div className="flex items-center gap-2">
                <Check className="size-4 shrink-0" />
                <span>Yes/No</span>
              </div>
            </SelectItem>
            <SelectItem value="Text">
              <div className="flex items-center gap-2">
                <span className="text-sm font-serif font-bold w-4 text-center shrink-0">T</span>
                <span>Text Input</span>
              </div>
            </SelectItem>
            <SelectItem value="TextArea">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold w-4 text-center shrink-0">=</span>
                <span>Text Area</span>
              </div>
            </SelectItem>
            <SelectItem value="Number">
              <div className="flex items-center gap-2">
                <Hash className="size-4 shrink-0" />
                <span>Number</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Field Label Input */}
        <Textarea
          placeholder="Field label"
          rows={2}
          value={field.label}
          onChange={(e) => onUpdate(field.id, { label: e.target.value })}
        />

        {/* Required Field Toggle */}
        <div className="flex items-center gap-2">
          <Switch
            checked={field.isRequired}
            onCheckedChange={(checked) =>
              onUpdate(field.id, { isRequired: !!checked })
            }
          />
          <span className="text-sm font-semibold text-content-primary-subtle">
            Required Field
          </span>
        </div>
      </div>

      {/* Delete Button */}
      {isArranging && (
        <button
          onClick={() => onRemove(field.id)}
          className="shrink-0 mt-0.5"
          aria-label="Delete field"
        >
          <Trash2 className="size-6 text-content-negative-bold" />
        </button>
      )}
    </Card>
  )
}
